import { motion } from 'framer-motion';
import { Compass, Map, Wrench, Rocket, Check, Lock } from 'lucide-react';

const ICONS = { discover_me: Compass, explore_my_future: Map, build_my_plan: Wrench, launch_mode: Rocket };

// Per-level accent so the path feels alive (not one flat color).
const ACCENT = {
  discover_me: { ring: '#0089FB', glow: 'rgba(0,137,251,0.55)' },
  explore_my_future: { ring: '#17CCF4', glow: 'rgba(23,204,244,0.55)' },
  build_my_plan: { ring: '#2FE0A8', glow: 'rgba(47,224,168,0.55)' },
  launch_mode: { ring: '#FFC24B', glow: 'rgba(255,194,75,0.6)' },
};

/**
 * JourneyMap, a bright, glowing progression path with an animated cyan
 * "progress glow" that fills across the levels the student has reached.
 */
export default function JourneyMap({ journey = [] }) {
  const n = journey.length || 1;
  const reachedCount = journey.filter((x) => x.status !== 'locked').length;
  // How far along the track the glow should fill (center of first -> center of last).
  const progressFrac = n > 1 ? Math.min(1, Math.max(0, (reachedCount - 1) / (n - 1))) : 1;
  const trackInset = 50 / n; // % from each edge to reach the first/last icon centers
  const trackWidth = 100 - 100 / n; // % between first and last centers
  const fillWidth = trackWidth * progressFrac; // % of full container

  return (
    <div className="relative pt-1" data-testid="journey-map">
      {/* Background track */}
      <div
        className="absolute top-8 h-1.5 rounded-full"
        style={{ left: `${trackInset}%`, width: `${trackWidth}%`, background: 'rgba(143,176,206,0.16)' }}
      />
      {/* Animated progress glow fill */}
      <motion.div
        className="absolute top-8 h-1.5 rounded-full overflow-hidden"
        style={{ left: `${trackInset}%`, background: 'linear-gradient(90deg,#0089FB,#17CCF4)', boxShadow: '0 0 16px rgba(23,204,244,0.75)' }}
        initial={{ width: 0 }}
        animate={{ width: `${fillWidth}%` }}
        transition={{ duration: 1.1, ease: 'easeOut' }}
      >
        {/* Traveling shimmer */}
        <motion.div
          className="absolute inset-y-0 w-1/3"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)' }}
          animate={{ x: ['-100%', '400%'] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>

      {/* Nodes */}
      <div className="relative flex items-start">
        {journey.map((node, i) => {
          const Icon = ICONS[node.key] || Compass;
          const done = node.status === 'completed';
          const active = node.status === 'active';
          const reached = node.status !== 'locked';
          const a = ACCENT[node.key] || ACCENT.discover_me;
          return (
            <motion.div
              key={node.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.09 }}
              className="relative z-10 flex-1 flex flex-col items-center text-center min-w-0"
              data-testid={`journey-node-${node.key}`}
            >
              <div
                className="relative w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-all"
                style={{
                  background: reached ? 'rgba(8,32,58,0.95)' : 'rgba(4,16,31,0.85)',
                  borderColor: a.ring,
                  color: a.ring,
                  // Every level glows, brighter when reached, subtle when locked.
                  boxShadow: reached ? `0 0 22px ${a.glow}, 0 0 4px ${a.glow}` : `0 0 10px ${a.glow.replace('0.5', '0.22').replace('0.6', '0.22').replace('0.55', '0.2')}`,
                  opacity: reached ? 1 : 0.75,
                }}
              >
                {done ? <Check className="w-6 h-6" /> : node.status === 'locked' ? <Lock className="w-4 h-4 opacity-80" /> : <Icon className="w-6 h-6" />}
                {active && (
                  <motion.span
                    className="absolute inset-0 rounded-2xl"
                    style={{ boxShadow: `0 0 0 3px ${a.glow}` }}
                    animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.08, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                  />
                )}
              </div>

              <div className="mt-2.5 px-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: a.ring, textShadow: `0 0 10px ${a.glow}` }}>
                  Level {node.level}
                </p>
                <p className="text-sm font-semibold truncate text-white">{node.name}</p>
                {node.total > 0 && node.key !== 'launch_mode' && (
                  <p className="text-[11px] mt-0.5 font-medium" style={{ color: done ? '#2FE0A8' : '#C6D4E8' }}>
                    {node.completed}/{node.total} done
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
